import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import './Contacts.css';

const Contacts = () => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchFilters, setSearchFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/contacts?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch contacts');
      }

      const data = await response.json();
      setContacts(data.contacts || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant) {
      fetchContacts();
    }
  }, [selectedTenant, fetchContacts]);

  const handleSearchChange = (columnField, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [columnField]: value.toLowerCase()
    }));
  };

  const clearAllFilters = () => {
    setSearchFilters({});
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sort = (contacts) => {
    return [...contacts].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filter = (contacts) => {
    return contacts.filter(contact => {
      return Object.entries(searchFilters).every(([field, searchValue]) => {
        if (!searchValue) return true;
        
        const itemValue = contact[field] || '';
        return String(itemValue).toLowerCase().includes(searchValue);
      });
    });
  };

  const filteredAndSortedContacts = sort(filter(contacts));

  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (field === 'email' && value.includes('@')) {
      return (
        <a href={`mailto:${value}`} className="email-link">
          {value}
        </a>
      );
    }
    
    if (field === 'phone' || field === 'mobile') {
      return value;
    }
    
    return value;
  };

  const getDisplayName = (fieldName) => {
    const displayNames = {
      'name': 'Name',
      'email': 'Email',
      'phone': 'Phone',
      'mobile': 'Mobile',
      'title': 'Title',
      'location': 'Location',
      'contact_type': 'Contact Type',
      'notes': 'Notes',
      'created_at': 'Created',
      'updated_at': 'Updated'
    };
    
    return displayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get column structure from first contact or create default structure
  const getColumns = () => {
    if (contacts.length === 0) {
      return [
        { field: 'name' },
        { field: 'email' },
        { field: 'phone' },
        { field: 'mobile' },
        { field: 'title' },
        { field: 'location' },
        { field: 'contact_type' },
        { field: 'notes' },
        { field: 'created_at' },
        { field: 'updated_at' }
      ];
    }
    
    return Object.keys(contacts[0]).map(field => ({ field }));
  };

  const columns = getColumns();

  // Filter out ID column and date columns
  const filteredColumns = columns.filter(column => {
    const fieldName = column.field.toLowerCase();
    return fieldName !== 'id' && 
           fieldName !== 'created_at' && 
           fieldName !== 'updated_at';
  });

  if (loading) {
    return (
      <div className="contacts-container">
        <div className="loading">Loading contacts...</div>
      </div>
    );
  }

  if (error) {
    const isITGlueCredentialsError = error.includes('IT Glue credentials not configured');
    
    return (
      <div className="contacts-container">
        <div className={isITGlueCredentialsError ? "info-message" : "error"}>
          <h3>{isITGlueCredentialsError ? "IT Glue Integration" : "Error loading contacts"}</h3>
          <p>{isITGlueCredentialsError ? "IT Glue credentials not configured for this tenant" : error}</p>
          {!isITGlueCredentialsError && (
            <button onClick={fetchContacts} className="retry-button">
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <div className="contacts-title-section">
          <h2>Contacts</h2>
          <div className="contacts-count">
            Showing {filteredAndSortedContacts.length} of {contacts.length} contacts
          </div>
        </div>
        
        <div className="contacts-actions">
          <button 
            onClick={clearAllFilters} 
            className="clear-filters-button"
            disabled={Object.keys(searchFilters).length === 0}
          >
            Clear Filters
          </button>
          <button onClick={fetchContacts} className="refresh-button">
            Refresh
          </button>
        </div>
      </div>

      <div className="contacts-table-container">
        <table className="contacts-table">
          <thead>
            <tr className="table-header">
              {filteredColumns.map(column => (
                <th 
                  key={column.field} 
                  className="table-header-cell"
                  onClick={() => handleSort(column.field)}
                >
                  <div className="header-content">
                    <span className="header-text">
                      {getDisplayName(column.field)}
                    </span>
                    <span className="sort-indicator">
                      {sortConfig.key === column.field && (
                        sortConfig.direction === 'asc' ? '↑' : '↓'
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
            
            <tr className="search-row">
              {filteredColumns.map(column => (
                <th key={`search-${column.field}`} className="search-header">
                  <input
                    type="text"
                    placeholder={`Search ${getDisplayName(column.field)}...`}
                    value={searchFilters[column.field] || ''}
                    onChange={(e) => handleSearchChange(column.field, e.target.value)}
                    className="column-search-input"
                  />
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {filteredAndSortedContacts.map((contact, index) => (
              <tr key={contact.id || index} className="table-row">
                {filteredColumns.map(column => {
                  const cellValue = contact[column.field];
                  const hasValue = cellValue && cellValue !== '';
                  
                  const handleCellClick = () => {
                    if (hasValue) {
                      // Set filter for this column
                      handleSearchChange(column.field, cellValue);
                    }
                  };
                  
                  return (
                    <td 
                      key={column.field} 
                      className={`table-cell ${hasValue ? 'clickable-cell' : ''}`}
                      onClick={handleCellClick}
                      style={{ 
                        cursor: hasValue ? 'pointer' : 'default' 
                      }}
                    >
                      {formatValue(contact[column.field], column.field)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredAndSortedContacts.length === 0 && (
          <div className="no-data">
            {Object.keys(searchFilters).length > 0 
              ? 'No contacts match your search criteria' 
              : 'No contacts found'
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default Contacts;
